import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import { CASE_STUDIES } from "@/data/caseStudies";

export default function CaseStudies() {
  return (
    <>
      <SEO
        title="Case Studies | Verification-first engineering in production"
        description="How United Techlab partners with founders and engineering leaders to ship modernization, application development, DevOps, cloud, and embedded engineering programs."
        path="/case-studies"
      />
      <PageHeader
        eyebrow="> /case_studies"
        title="Engineering programs we own end-to-end."
        subtitle="Real partnerships with real outcomes: modernization, application development, embedded engineering, DevOps, and cloud."
      />

      <section
        className="relative border-b border-white/10 bg-obsidian"
        data-testid="case-studies-list"
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {CASE_STUDIES.map((cs, i) => (
              <motion.div
                key={cs.slug}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="bg-carbon p-8 lg:p-10 group flex flex-col"
                data-testid={`case-study-card-${cs.slug}`}
              >
                <div className="flex items-center justify-between gap-4 mb-6">
                  <span
                    className={`chip ${
                      cs.accent === "green" ? "text-neon-green" : "text-neon-cyan"
                    }`}
                  >
                    // {cs.industry}
                  </span>
                  <span className="text-[11px] font-mono text-white/40 uppercase tracking-wider">
                    {cs.duration}
                  </span>
                </div>

                <h2 className="font-display font-black text-3xl md:text-4xl text-white tracking-tight leading-tight">
                  {cs.client}
                </h2>

                <p className="mt-4 text-white/70 text-sm leading-relaxed">
                  {cs.headline}
                </p>

                <ul className="mt-6 flex flex-wrap gap-2">
                  {cs.services.map((s) => (
                    <li
                      key={s}
                      className="text-[11px] font-mono text-white/60 border border-white/10 px-2.5 py-1"
                    >
                      {s}
                    </li>
                  ))}
                </ul>

                <div className="mt-8 grid grid-cols-2 gap-4 pt-6 border-t border-white/10">
                  {cs.outcomes.slice(0, 2).map((o) => (
                    <div key={o.label}>
                      <div
                        className={`font-display font-black text-2xl ${
                          cs.accent === "green"
                            ? "text-neon-green"
                            : "text-neon-cyan"
                        }`}
                      >
                        {o.value}
                      </div>
                      <div className="mt-1 text-[11px] font-mono text-white/50 leading-tight">
                        {o.label}
                      </div>
                    </div>
                  ))}
                </div>

                <Link
                  to={`/case-studies/${cs.slug}`}
                  data-testid={`case-study-link-${cs.slug}`}
                  className={`mt-auto pt-8 inline-flex items-center gap-2 font-mono text-sm uppercase tracking-wider self-start ${
                    cs.accent === "green"
                      ? "text-neon-green hover:text-glow-green"
                      : "text-neon-cyan hover:text-glow-cyan"
                  } transition-all`}
                >
                  Read case study <ArrowRight size={16} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="A program like one of these in mind?"
        subtitle="We pick partnerships carefully and over-deliver on the ones we take. 30-minute call with the founder, no sales floor."
        ctaLabel="Start a conversation"
      />
    </>
  );
}
